/**
 * The agent's side of a mandate: the Request Representation form and the list
 * of what this account has already asked for.
 *
 * The owner's side — accepting or declining — is the verifications inbox's
 * third tab and lives in `verifications.model.ts`; only the copy for it sits
 * beside this file, in `representations.constants.ts`. The reads and writes
 * are in `useRequestRepresentationController.ts`.
 *
 * This model validates almost nothing on purpose. The code and the confirming
 * year are checked in SQL as one opaque answer, and a client-side opinion on
 * either would tell the caller which field was wrong — the one thing the door
 * is arranged never to say.
 */

import { wasRepresentationAnswered } from "@ondo/shared";
import type {
  RepresentationOutcome,
  RepresentationRequestMade,
  RequestRepresentationResponse,
} from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { OUTCOME_LABELS, REQUEST_COPY } from "./representations.constants.ts";

/**
 * The Request Representation screen, decided.
 *
 * The form is ALWAYS rendered, which is why `loading` and `error` are states of
 * the LIST rather than of the screen. An agent arriving to ask about a work they
 * have just been told about should not be made to wait on a read of their own
 * history, and a failure to load that history is no reason to withhold the one
 * control the screen exists for.
 */
export interface RequestFormView {
  code: string;
  confirm: string;
  note: string;
  /** Both fields carry something. The server decides everything else. */
  canSubmit: boolean;
  busy: boolean;
  /** What the last attempt said, or null before there was one. */
  outcome: RequestOutcome | null;
}

export type RequestOutcome =
  | { kind: "sent"; message: string }
  | { kind: "refused"; message: string };

export type MadeListView =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  | { kind: "empty"; message: string; detail: string }
  | { kind: "rows"; rows: MadeRow[] };

export interface MadeRow {
  requestId: string;
  ondoCode: string;
  title: string;
  attributedName: string;
  note: string | null;
  createdAt: string;
  /** The badge: "Awaiting the owner", or how it ended. */
  status: string;
  /** Still open, so the row reads as a question rather than as history. */
  open: boolean;
  responseNote: string | null;
  /** The database's answer, never this file's — see `RepresentationRequestMade`. */
  canWithdraw: boolean;
}

/**
 * Whether the form may be sent.
 *
 * BOTH FIELDS AND NOTHING ELSE. There is deliberately no client-side check that
 * the code looks like a code or that the confirmation looks like a year: every
 * refusal is one opaque answer decided in SQL, and a second opinion here would
 * produce a form that refuses something the server would have accepted — or,
 * worse, tells the caller which of the two fields was wrong, which is the one
 * thing the whole door is arranged not to say.
 */
export function canSubmitRequest(input: {
  code: string;
  confirm: string;
  busy: boolean;
}): boolean {
  return (
    !input.busy && input.code.trim().length > 0 && input.confirm.trim().length > 0
  );
}

/**
 * What the server said, as a sentence.
 *
 * `refused` is a verdict and not a failure — see `requestRepresentation` in
 * `lib/api.ts` — so it arrives here as data. Both branches render in the same
 * place with the same weight, because a mistyped code is an ordinary outcome of
 * this form and not an error the agent did something wrong to cause.
 */
export function outcomeOf(response: RequestRepresentationResponse): RequestOutcome {
  if (response.verdict === "refused" || !response.workTitle) {
    return { kind: "refused", message: REQUEST_COPY.refused };
  }

  return { kind: "sent", message: REQUEST_COPY.sent(response.workTitle) };
}

/**
 * A thrown failure, as a sentence.
 *
 * 429 IS ITS OWN BRANCH and must stay one. The controller answers 403 for a
 * refusal and 429 for the hour's budget precisely so the two can be told apart,
 * and showing "no such code" to somebody who was throttled would tell them the
 * opposite of the truth about the guess they just made.
 */
export function describeRequestError(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 429) return REQUEST_COPY.throttled;
  if (cause instanceof ApiError && cause.message) return cause.message;

  return REQUEST_COPY.error;
}

export function toMadeList(input: {
  requests: RepresentationRequestMade[] | null;
  error: string | null;
}): MadeListView {
  if (input.error) return { kind: "error", message: input.error };
  if (!input.requests) return { kind: "loading", message: REQUEST_COPY.madeLoading };
  if (input.requests.length === 0) {
    return { kind: "empty", message: REQUEST_COPY.madeEmpty, detail: REQUEST_COPY.lead };
  }

  return { kind: "rows", rows: input.requests.map(toMadeRow) };
}

function toMadeRow(request: RepresentationRequestMade): MadeRow {
  return {
    requestId: request.requestId,
    ondoCode: request.ondoCode,
    title: request.title,
    attributedName: request.attributedName,
    note: request.note,
    createdAt: request.createdAt,
    status: statusOf(request.outcome),
    open: request.outcome === null,
    responseNote: request.responseNote,
    canWithdraw: request.canWithdraw,
  };
}

/**
 * The badge.
 *
 * Falls back to the raw value rather than throwing on an outcome this build has
 * never heard of. The API and the schema are versioned by deployment, and a
 * screen that crashed on a value added since it was built is worse than one that
 * shows the word.
 */
export function statusOf(outcome: RepresentationOutcome | null): string {
  if (outcome === null) return REQUEST_COPY.awaiting;

  return OUTCOME_LABELS[outcome] ?? outcome;
}

/** Whether the owner has answered, as opposed to the agent having taken it back. */
export function wasAnswered(outcome: RepresentationOutcome | null): boolean {
  return wasRepresentationAnswered(outcome);
}
